import { computed, inject } from 'vue'
import { isCondition, parseCondition } from '@shared/utils/json-logic'

export function useUserActions(column, node) {
  const pageVarsQuery = inject('pageVarsQuery')
  const userActions = computed(() => pageVarsQuery.pageVars?.value?.availableUserActions || [])

  function shouldAddAction({ privilege, role, allow }, data, companyPrivileges, companyRoles) {
    if (privilege && !companyPrivileges.includes(privilege)) return false
    if (role && !companyRoles.includes(role)) return false

    if (allow) {
      return Object.entries(allow).every(([field, values]) => {
        const rowValue = data[field]
        return rowValue && values.includes(rowValue)
      })
    }

    return true
  }

  function processJsonLogicUserAction(userAction, data) {
    const result = {}
    const user = pageVarsQuery.pageVars?.value?.user || window.siteVars?.user || {}
    const lookup = { ...data, user }

    for (const [key, value] of Object.entries(userAction)) {
      result[key] = value
      try {
        if (isCondition(value)) result[key] = parseCondition(value, lookup)
      } catch (e) {
        console.warn(`[Datagrid] Error processing JSON logic for userAction: ${userAction.name}, key: ${key}`, e)
      }
    }

    return result.visible !== false ? result : null
  }

  try {
    const companyPrivileges = window.siteVars?.user?.userCompanyPrivileges || []
    const companyRoles = window.siteVars?.user?.userCompanyRoles || []
    const colDef = column?.userProvidedColDef
    const field = column?.colDef?.field
    const data = node?.data || {}

    if (pageVarsQuery.isPending.value) return [{ name: 'Loading actions...', disabled: true }]

    let userActionsCopy = []

    if (colDef?.cellRendererParams?.userActions) {
      userActionsCopy = colDef.cellRendererParams.userActions
        .map((action) => processJsonLogicUserAction(action, data))
        .filter(Boolean)
    } else {
      const cellRendererParams = colDef?.cellRendererParams || {}
      const overrideKeys = Object.keys(cellRendererParams)

      userActionsCopy = userActions.value.map((action) => {
        const override = overrideKeys.find((key) => key === action.name)
        if (!override) return { ...action }
        const params = cellRendererParams[override]
        return shouldAddAction(params, data, companyPrivileges, companyRoles)
          ? { ...action, label: params.label, cssClasses: params.cssClasses }
          : null
      }).filter(Boolean)

      overrideKeys.forEach((workflowName) => {
        if (userActions.value.some((action) => action.name === workflowName)) return
        const params = cellRendererParams[workflowName]

        if (params.cssClasses && !Array.isArray(params.cssClasses)) {
          console.warn(
            `[Datagrid] cssClasses for ${workflowName} should be an array in columnDefs for field ${field}`,
          )
        }
        if (shouldAddAction(params, data, companyPrivileges, companyRoles)) {
          userActionsCopy.unshift({
            name: workflowName,
            label: params.label ?? workflowName,
            cssClasses: params.cssClasses || [],
          })
        }
      })
    }

    return userActionsCopy.length
      ? userActionsCopy.map((action) => {
          const actionUrl = `/Useraction/1/${action.name}/Start?appIds=${data[field] || ''}`
          return {
            name: action.label || action.name,
            link: actionUrl,
            cssClasses: action.cssClasses,
            disabled: action.disabled || false,
            action() {
              if (!action.disabled) window.location.href = actionUrl
            },
          }
        })
      : [{ name: 'No actions available', disabled: true }]
  } catch (e) {
    console.error('[Datagrid] Error computing user actions:', e)
    return [{ name: 'Error loading actions', disabled: true }]
  }
}
